import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Mail, ShieldCheck, Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";
import ThemeToggle from "@/components/ThemeToggle";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Accesso non riuscito");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-[100dvh] overflow-y-auto w-full flex bg-background">
      <div className="hidden lg:flex flex-col justify-between w-[45%] bg-slate-900 text-white p-12 relative overflow-hidden">
        <div className="absolute -right-20 -top-20 h-80 w-80 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="absolute -left-10 bottom-10 h-72 w-72 rounded-full bg-sky-400/10 blur-3xl" />
        <div className="flex items-center gap-3 relative z-10">
          <div className="h-11 w-11 rounded-2xl bg-sky-500 flex items-center justify-center shadow-lg">
            <Building2 className="h-6 w-6 text-white" />
          </div>
          <span className="text-xl font-bold tracking-tight">Relatio</span>
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="text-4xl font-extrabold leading-tight tracking-tight">
            Gestione riservata di <span className="text-sky-400">fornitori e clienti</span>.
          </h1>
          <p className="text-slate-300 text-base leading-relaxed max-w-sm">
            Anagrafiche, referenti e note operative del tuo portafoglio, sempre disponibili per il team autorizzato.
          </p>
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <ShieldCheck className="h-4 w-4 text-sky-400" />
            Accesso riservato ai membri autorizzati
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-500">© {new Date().getFullYear()} Relatio</div>
      </div>

      <div className="flex-1 flex flex-col">
        <div className="flex justify-end p-4">
          <ThemeToggle />
        </div>
        <div className="flex-1 flex items-center justify-center px-6 pb-16">
          <div className="w-full max-w-sm">
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <div className="h-10 w-10 rounded-2xl bg-sky-500 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <span className="text-lg font-bold">Relatio</span>
            </div>
            <h2 className="text-2xl font-bold tracking-tight">Bentornato</h2>
            <p className="text-muted-foreground text-sm mt-1 mb-8">Accedi al tuo account per continuare.</p>

            <form onSubmit={submit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    required
                    data-testid="login-email-input"
                    className="pl-10 h-11"
                    placeholder="nome@azienda.it"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    required
                    data-testid="login-password-input"
                    className="pl-10 h-11"
                    placeholder="••••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="w-full h-11 bg-sky-600 hover:bg-sky-700 text-white font-semibold"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accedi"}
              </Button>
            </form>

            <p className="text-sm text-muted-foreground mt-6 text-center">
              Hai un codice invito?{" "}
              <Link to="/register" data-testid="go-register-link" className="text-sky-600 font-semibold hover:underline">
                Registrati
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
